import {
    TextInput,
    PasswordInput,
    Checkbox,
    Anchor,
    Paper,
    Title,
    Text,
    Container,
    Group,
    Button,
} from '@mantine/core';
import logo from '../../assets/Long_logo_dark.svg';
import { useEffect, useState, useRef } from 'react';
import axios from '../../api/axios.js'
import useAuth from '../../hooks/useAuth';
import { useParams } from 'react-router-dom';

const USER_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%]).{8,24}$/;
const RESET_URL = '/password';

export default function PasswordReset() {

    const { persist, setPersist } = useAuth();
    const { user, token } = useParams();

    const derivedParams = (() => {
        const rawPath = String(window?.location?.pathname || "");
        const parts = rawPath.split("/").filter(Boolean);
        const idx = parts.indexOf("forgot-password");
        if (idx === -1) return { user: null, token: null };
        const u = parts[idx + 1] ? decodeURIComponent(parts[idx + 1]) : null;
        const t = parts[idx + 2] ? decodeURIComponent(parts[idx + 2]) : null;
        return { user: u, token: t };
    })();

    const effectiveUser = user || derivedParams.user;
    const effectiveToken = token || derivedParams.token;

    const errRef = useRef();

    const [pwd, setPwd] = useState('');
    const [validPwd, setValidPwd] = useState(false);

    const [matchPwd, setMatchPwd] = useState('');
    const [validMatch, setValidMatch] = useState(false);
    const [validMatchPwd, setValidMatchPwd] = useState(false);

    useEffect(() => {
        // Check if password meets complexity criteria
        const hasValidLength = matchPwd.length >= 8;
        const hasUppercase = /[A-Z]/.test(matchPwd);
        const hasSpecialChar = /[\W_]/.test(matchPwd);
        const hasNumber = /\d/.test(matchPwd);

        // Set validMatchPwd based on complexity criteria
        setValidMatchPwd(hasValidLength && hasUppercase && hasSpecialChar && hasNumber);
    }, [matchPwd]);

    const [errMsg, setErrMsg] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        setValidPwd(PWD_REGEX.test(pwd));
        setValidMatch(pwd === matchPwd);
    }, [pwd, matchPwd])

    useEffect(() => {
        setErrMsg('');
    }, [pwd, matchPwd])

    useEffect(() => {
        localStorage.setItem("persist", persist);
    }, [persist])

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!effectiveUser || !effectiveToken) {
            setErrMsg("Invalid or expired reset link.");
            return;
        }

        const v2 = PWD_REGEX.test(pwd);
        if (!v2) {
            setErrMsg("Invalid Entry");
            return;
        }
        try {
            const response = await axios.put(RESET_URL,
                JSON.stringify({ user: effectiveUser, token: effectiveToken, password: pwd }),
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true
                });
            console.log(response.data)
            console.log(JSON.stringify(response));
            setSuccess(true);
        } catch (err) {
            console.error(err)
            if (!err?.response) {
                setErrMsg("No server response")
            } else if (err.response?.status === 409) {
                setErrMsg("Username already taken")
            } else {
                setErrMsg("Registration failed. Unknown error.")
            }
            setErrMsg(JSON.stringify(err))
            // errRef.current.focus();
        }
    }

    return (
        <>
            {success ? (
                <Container size={420} my={40}>
                    <Title ta="center">
                        Password reset successfully!
                    </Title>
                    <Text c="dimmed" size="sm" ta="center" mt={5}>
                        Go to the login page{' '}
                        <Anchor size="sm" component="a" href="/login">
                            Login
                        </Anchor>
                    </Text>
                </Container>
            ) : (
                <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f7f9fc' }}>
                    <Container size={420} my={40}>
                        <Paper
                            withBorder
                            shadow="md"
                            p={30}
                            radius="md"
                            style={{ backgroundColor: '#fff' }}
                        >
                            <Title ta="center" style={{ marginBottom: 24 }}>
                                Reset password
                            </Title>

                            {!effectiveUser || !effectiveToken ? (
                                <Text c="red" size="sm" ta="center" mt={10}>
                                    Invalid or expired reset link.
                                </Text>
                            ) : null}

                            <form onSubmit={handleSubmit}>
                                <PasswordInput
                                    label="New password"
                                    placeholder="Your password"
                                    required mt="md"
                                    value={pwd}
                                    onChange={(e) => setPwd(e.target.value)}
                                    aria-describedby="pwdnote" />
                                <PasswordInput
                                    label="Confirm password"
                                    placeholder="Confirm password"
                                    required mt="md"
                                    value={matchPwd}
                                    onChange={(e) => setMatchPwd(e.target.value)}
                                    aria-describedby="pwdnote" />

                                <Text c="red" size="sm" mt={14}>
                                    <span className={!validMatchPwd ? "invalid" : "hide"}>
                                        {matchPwd.length < 8 && "Password must be at least 8 characters long. "}
                                        {matchPwd.length >= 8 && !/[A-Z]/.test(matchPwd) && "Password must contain at least one uppercase letter. "}
                                        {matchPwd.length >= 8 && !/[\W_]/.test(matchPwd) && "Password must contain at least one special character. "}
                                        {matchPwd.length >= 8 && !/\d/.test(matchPwd) && "Password must contain at least one number. "}
                                    </span>
                                </Text>

                                <Button type="submit" variant="outline" color="resirent" fullWidth mt="lg" disabled={!validPwd || !validMatch ? true : false}>
                                    Reset Password
                                </Button>
                            </form>
                        </Paper>
                    </Container>
                </div>
            )}
        </>
    );
}